import type { PositionView } from '@/types/myBoards';
import { cn } from '@/lib/cn';

/**
 * Il tabellone STATICO di una posizione di «My Boards» (T4 miniatura, T5 grande): un SVG che disegna un `PositionView`, e
 * niente altro. Nessun gestore di tocco, nessun hook, nessuna rete: la posizione e' un dato congelato (ADR-0191 §1).
 *
 * Non e' `board-ui` e non e' `ReplayBoard` (che prende un `GameState` col tiro consumato e le due dimensioni del replay):
 * la tavola T5 vuole le punte numerate, il tiro in mano sul tabellone e il cubo sulla barra, a due scale con lo stesso
 * disegno. Stessa geometria del tabellone vivo, vista dal Bianco (casa in basso a destra): in alto le punte 13..24, in
 * basso 12..1, indice i = punta i+1, segno positivo = Bianco.
 *
 * Colori dai gettoni del tema (`fill-fg`, `fill-bg`, `stroke-border`…), nessun esadecimale: il Bianco e' il gettone chiaro,
 * il Nero il gettone scuro col bordo.
 *
 * ⚠️ Il tabellone non dice niente che la posizione non dica: se `phase` e' `moving` mostra il tiro, altrimenti no. Il
 * cubo si disegna solo se e' NOTO e ha un proprietario (centrato o ignoto: lo dice la pastiglia sotto, non il tabellone).
 * Niente etichetta «Crawford»: nella partita da un board il cubo e' spento.
 */

const PAD = 8;
const PW = 30; // larghezza di una punta
const HALF = PW * 6;
const BAR = 24;
const TRAY = 26;
const PH = 110; // altezza di una punta
const TOP = 14;
const BOTTOM = 286;
const R = 12.5; // raggio di una pedina
const CENTRO_Y = (TOP + PH + (BOTTOM - PH)) / 2;
const LARGHEZZA = PAD + HALF + BAR + HALF + 4 + TRAY + PAD;
const ALTEZZA = BOTTOM + 14;
const X_BARRA = PAD + HALF + BAR / 2;
const X_VASSOIO = PAD + HALF + BAR + HALF + 4;
const MAX_PEDINE = 5;
const MAX_SULLA_BARRA = 3;

/** La x sinistra della punta nello slot `s` (0..11): le prime sei a sinistra della barra, le altre a destra. */
function xDello(s: number): number {
  return s < 6 ? PAD + s * PW : PAD + HALF + BAR + (s - 6) * PW;
}

/** Quale punta (indice 0..23) sta nello slot `s` di una riga. In alto 13..24, in basso 12..1. */
function indiceDello(riga: 'alto' | 'basso', s: number): number {
  return riga === 'alto' ? 12 + s : 11 - s;
}

function Pedina({ lato, cx, cy, conto }: { lato: 'W' | 'B'; cx: number; cy: number; conto?: number }) {
  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r={R}
        className={lato === 'W' ? 'fill-fg stroke-border' : 'fill-bg stroke-fg-muted'}
        strokeWidth={1.5}
      />
      {conto !== undefined && (
        <text
          x={cx}
          y={cy + 4}
          textAnchor="middle"
          fontSize={11}
          fontWeight={700}
          className={lato === 'W' ? 'fill-bg' : 'fill-fg'}
        >
          {conto}
        </text>
      )}
    </g>
  );
}

function Dado({ valore, x, lato }: { valore: number; x: number; lato: 'W' | 'B' }) {
  return (
    <g>
      <rect
        x={x}
        y={CENTRO_Y - 12}
        width={24}
        height={24}
        rx={5}
        className={lato === 'W' ? 'fill-fg stroke-border' : 'fill-bg stroke-fg-muted'}
        strokeWidth={1.5}
      />
      <text
        x={x + 12}
        y={CENTRO_Y + 5}
        textAnchor="middle"
        fontSize={15}
        fontWeight={800}
        className={lato === 'W' ? 'fill-bg' : 'fill-fg'}
      >
        {valore}
      </text>
    </g>
  );
}

export function PosizioneBoard({
  position,
  size = 'large',
  ariaLabel,
  className,
}: {
  position: PositionView;
  /** `mini`: la miniatura della lista (senza numeri delle punte ne' tiro); `large`: la scheda. */
  size?: 'mini' | 'large';
  ariaLabel: string;
  className?: string;
}) {
  const grande = size === 'large';
  const punte: React.ReactNode[] = [];
  const pedine: React.ReactNode[] = [];
  const numeri: React.ReactNode[] = [];

  for (const riga of ['alto', 'basso'] as const) {
    for (let s = 0; s < 12; s++) {
      const i = indiceDello(riga, s);
      const x = xDello(s);
      const alto = riga === 'alto';
      const y0 = alto ? TOP : BOTTOM;
      const y1 = alto ? TOP + PH : BOTTOM - PH;
      punte.push(
        <polygon
          key={`p-${riga}-${s}`}
          points={`${x},${y0} ${x + PW},${y0} ${x + PW / 2},${y1}`}
          className={s % 2 === 0 ? 'fill-fg-muted/35' : 'fill-bg-tertiary'}
        />,
      );
      if (grande) {
        numeri.push(
          <text
            key={`n-${riga}-${s}`}
            x={x + PW / 2}
            y={alto ? 10 : ALTEZZA - 1}
            textAnchor="middle"
            fontSize={8}
            className="fill-fg-muted"
          >
            {i + 1}
          </text>,
        );
      }
      const cella = position.points[i] ?? 0;
      if (cella === 0) continue;
      const lato = cella > 0 ? 'W' : 'B';
      const totale = Math.abs(cella);
      const visibili = Math.min(totale, MAX_PEDINE);
      for (let k = 0; k < visibili; k++) {
        const cy = alto ? y0 + R + k * 2 * R : y0 - R - k * 2 * R;
        pedine.push(
          <Pedina
            key={`c-${riga}-${s}-${k}`}
            lato={lato}
            cx={x + PW / 2}
            cy={cy}
            conto={k === visibili - 1 && totale > MAX_PEDINE ? totale : undefined}
          />,
        );
      }
    }
  }

  // Sulla barra: il Nero in alto, il Bianco in basso (vista del Bianco), a partire dal centro.
  const sullaBarra: React.ReactNode[] = [];
  for (const lato of ['B', 'W'] as const) {
    const n = position.bar[lato];
    const visibili = Math.min(n, MAX_SULLA_BARRA);
    for (let k = 0; k < visibili; k++) {
      const passo = 2 * R + 1;
      const cy = lato === 'B' ? CENTRO_Y - R - 6 - k * passo : CENTRO_Y + R + 6 + k * passo;
      sullaBarra.push(
        <Pedina
          key={`b-${lato}-${k}`}
          lato={lato}
          cx={X_BARRA}
          cy={cy}
          conto={k === visibili - 1 && n > MAX_SULLA_BARRA ? n : undefined}
        />,
      );
    }
  }

  // Le uscite: barrette piatte impilate nel vassoio a destra, il Nero in alto e il Bianco in basso.
  const uscite: React.ReactNode[] = [];
  for (const lato of ['B', 'W'] as const) {
    for (let k = 0; k < position.off[lato]; k++) {
      const y = lato === 'B' ? TOP + k * 7 : BOTTOM - 6 - k * 7;
      uscite.push(
        <rect
          key={`o-${lato}-${k}`}
          x={X_VASSOIO + 3}
          y={y}
          width={TRAY - 6}
          height={6}
          rx={1.5}
          className={lato === 'W' ? 'fill-fg stroke-border' : 'fill-bg stroke-fg-muted'}
          strokeWidth={1}
        />,
      );
    }
  }

  // Il cubo: solo se noto e posseduto (centrato o ignoto lo dice la pastiglia della scheda).
  const { cube } = position;
  const cubo =
    cube.known && cube.owner !== null ? (
      <g>
        <rect
          x={X_BARRA - 10}
          y={cube.owner === 'W' ? BOTTOM - 22 : TOP + 2}
          width={20}
          height={20}
          rx={4}
          className="fill-fg stroke-brand"
          strokeWidth={1.5}
        />
        <text
          x={X_BARRA}
          y={(cube.owner === 'W' ? BOTTOM - 22 : TOP + 2) + 14}
          textAnchor="middle"
          fontSize={12}
          fontWeight={800}
          className="fill-bg"
        >
          {cube.value}
        </text>
      </g>
    ) : null;

  // Il tiro in mano, sulla meta' destra, nel colore di chi e' di turno.
  const tiro =
    grande && position.phase === 'moving' && position.roll !== null ? (
      <g>
        <Dado valore={position.roll[0]} x={PAD + HALF + BAR + HALF / 2 - 28} lato={position.turn} />
        <Dado valore={position.roll[1]} x={PAD + HALF + BAR + HALF / 2 + 4} lato={position.turn} />
      </g>
    ) : null;

  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      viewBox={`0 0 ${LARGHEZZA} ${ALTEZZA}`}
      className={cn('block h-auto w-full select-none', className)}
    >
      <rect x={0} y={0} width={LARGHEZZA} height={ALTEZZA} rx={10} className="fill-bg-secondary stroke-border" strokeWidth={1} />
      <rect x={PAD + HALF} y={TOP - 4} width={BAR} height={BOTTOM - TOP + 8} className="fill-bg" />
      <rect x={X_VASSOIO} y={TOP - 4} width={TRAY} height={BOTTOM - TOP + 8} rx={3} className="fill-bg" />
      {punte}
      {numeri}
      {pedine}
      {sullaBarra}
      {uscite}
      {cubo}
      {tiro}
    </svg>
  );
}
